import { Link } from "@tanstack/react-router";
import { Clock, Tag } from "lucide-react";
import type { ReactNode } from "react";

/**
 * Shared hero for every service page: breadcrumb (Hem / Tjänster / <name>),
 * two-column grid with a square image, price/duration chips and the standard
 * brass CTA + "Se alla salonger" outline button.
 *
 * ServicePageTemplate and the standalone SEO service routes (taper-fade,
 * skin-fade, skaggtrimning, seniorklippning...) all render this so
 * the layout stays identical.
 */
export function ServiceHero({
  breadcrumbName,
  eyebrow,
  title,
  directAnswerHeading,
  directAnswer,
  intro,
  priceLabel,
  durationLabel,
  ctaLabel,
  ctaHref,
  onCtaClick,
  image,
  imageAlt,
  eager = false,
}: {
  breadcrumbName: string;
  eyebrow: string;
  title: ReactNode;
  directAnswerHeading?: string;
  directAnswer?: ReactNode;
  intro: ReactNode;
  priceLabel: string;
  durationLabel?: string;
  ctaLabel: string;
  ctaHref: string;
  onCtaClick?: () => void;
  image: string;
  imageAlt: string;
  eager?: boolean;
}) {
  return (
    <section className="container-page pt-10 md:pt-16 pb-12 md:pb-20">
      <nav className="text-xs text-muted-foreground mb-6">
        <Link to="/" className="hover:underline">
          Hem
        </Link>
        <span className="mx-1.5">/</span>
        <span>Tjänster</span>
        <span className="mx-1.5">/</span>
        <span className="text-foreground">{breadcrumbName}</span>
      </nav>

      <div className="grid lg:grid-cols-[3fr_2fr] gap-10 lg:gap-16 items-center">
        <div>
          <div className="eyebrow">{eyebrow}</div>
          <h1 className="mt-3 text-4xl md:text-5xl lg:text-6xl leading-[1.05]">{title}</h1>
          {directAnswerHeading && directAnswer ? (
            <div className="mt-6 max-w-prose">
              <h2 className="text-2xl md:text-3xl">{directAnswerHeading}</h2>
              <p className="mt-3 text-lg text-foreground/85 leading-relaxed">{directAnswer}</p>
            </div>
          ) : null}
          <p className="mt-5 text-lg text-foreground/75 max-w-prose leading-relaxed">{intro}</p>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <span className="inline-flex items-center gap-1.5">
              <Tag size={16} /> <strong>{priceLabel}</strong>
            </span>
            {durationLabel ? (
              <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                <Clock size={16} /> {durationLabel}
              </span>
            ) : null}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={ctaHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onCtaClick}
              className="btn-brass"
            >
              {ctaLabel}
            </a>
            <Link to="/" className="btn-outline">
              Se alla salonger
            </Link>
          </div>
        </div>
        <div className="aspect-square rounded-2xl overflow-hidden bg-muted shadow-soft">
          <img
            src={image}
            alt={imageAlt}
            width={1080}
            height={1080}
            loading={eager ? "eager" : "lazy"}
            className="h-full w-full object-cover"
          />
        </div>
      </div>
    </section>
  );
}
